<script setup lang="ts">
/**
 * Cookie Settings Modal - Laumeris
 * Modal de paramétrage granulaire des cookies
 * Conforme CNIL/RGPD
 */

import { ref, watch } from 'vue'
import { useCookiesStore, type CookiePreferences } from '@/stores/cookies'

const cookiesStore = useCookiesStore()

// Préférences locales pour modification
const localPreferences = ref<CookiePreferences>({
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false
})

// Synchroniser avec le store
watch(() => cookiesStore.showSettings, (show) => {
  if (show) {
    localPreferences.value = { ...cookiesStore.preferences }
  }
})

// Sauvegarder les préférences
function savePreferences() {
  cookiesStore.savePreferences(localPreferences.value)
}

// Tout accepter
function acceptAll() {
  localPreferences.value = {
    necessary: true,
    analytics: true,
    marketing: true,
    preferences: true
  }
  cookiesStore.savePreferences(localPreferences.value)
}

// Tout refuser (sauf nécessaires)
function rejectAll() {
  localPreferences.value = {
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false
  }
  cookiesStore.savePreferences(localPreferences.value)
}

// Fermer le modal
function close() {
  cookiesStore.closeSettings()
  cookiesStore.closeBanner()
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="cookiesStore.showSettings"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        <!-- Overlay -->
        <div 
          class="absolute inset-0 bg-black/50 backdrop-blur-sm"
          @click="close"
        />
        
        <!-- Modal -->
        <Transition
          enter-active-class="transition-all duration-300"
          enter-from-class="scale-95 opacity-0"
          enter-to-class="scale-100 opacity-100"
          leave-active-class="transition-all duration-200"
          leave-from-class="scale-100 opacity-100"
          leave-to-class="scale-95 opacity-0"
        >
          <div 
            v-if="cookiesStore.showSettings"
            class="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-corporate-900 rounded-2xl shadow-2xl"
          >
            <!-- Header -->
            <div class="sticky top-0 z-10 bg-white dark:bg-corporate-900 border-b border-corporate-200 dark:border-corporate-800 px-6 py-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1E3A5F] to-[#152238] flex items-center justify-center">
                    <svg class="w-5 h-5 text-[#C9B896]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 class="text-lg font-semibold text-corporate-900 dark:text-white">
                      Paramètres des cookies
                    </h2>
                    <p class="text-sm text-corporate-500 dark:text-corporate-400">
                      Gérez vos préférences de confidentialité
                    </p>
                  </div>
                </div>
                <button
                  @click="close"
                  class="p-2 text-corporate-400 hover:text-corporate-600 dark:hover:text-corporate-300 transition-colors rounded-lg hover:bg-corporate-100 dark:hover:bg-corporate-800"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Content -->
            <div class="p-6 space-y-6">
              <!-- Introduction -->
              <div class="bg-corporate-50 dark:bg-corporate-800/50 rounded-xl p-4">
                <p class="text-sm text-corporate-600 dark:text-corporate-400">
                  Nous utilisons des cookies pour améliorer votre expérience sur notre site. 
                  Vous pouvez choisir les types de cookies que vous acceptez. 
                  <router-link to="/legal/cookies" class="text-accent-600 hover:underline" @click="close">
                    Voir notre politique de cookies
                  </router-link>
                </p>
              </div>

              <!-- Cookie Categories -->
              <div class="space-y-4">
                
                <!-- Cookies Nécessaires -->
                <div class="border border-corporate-200 dark:border-corporate-700 rounded-xl overflow-hidden">
                  <div class="p-4 bg-success-50 dark:bg-success-900/20">
                    <div class="flex items-start justify-between gap-4">
                      <div class="flex-1">
                        <div class="flex items-center gap-2 mb-1">
                          <h3 class="font-semibold text-corporate-900 dark:text-white">
                            Cookies strictement nécessaires
                          </h3>
                          <span class="px-2 py-0.5 text-xs font-medium bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-400 rounded">
                            Toujours actifs
                          </span>
                        </div>
                        <p class="text-sm text-corporate-600 dark:text-corporate-400">
                          Ces cookies sont essentiels au fonctionnement du site. Ils permettent l'authentification, 
                          la sécurité et les fonctionnalités de base. Ils ne peuvent pas être désactivés.
                        </p>
                      </div>
                      <div class="flex-shrink-0">
                        <div class="w-12 h-7 bg-success-500 rounded-full flex items-center justify-end px-1 cursor-not-allowed opacity-75">
                          <div class="w-5 h-5 bg-white rounded-full shadow"></div>
                        </div>
                      </div>
                    </div>
                    <div class="mt-3 flex flex-wrap gap-2">
                      <span class="px-2 py-1 text-xs bg-white dark:bg-corporate-800 text-corporate-600 dark:text-corporate-400 rounded border border-corporate-200 dark:border-corporate-700">
                        Authentification
                      </span>
                      <span class="px-2 py-1 text-xs bg-white dark:bg-corporate-800 text-corporate-600 dark:text-corporate-400 rounded border border-corporate-200 dark:border-corporate-700">
                        Sécurité
                      </span>
                      <span class="px-2 py-1 text-xs bg-white dark:bg-corporate-800 text-corporate-600 dark:text-corporate-400 rounded border border-corporate-200 dark:border-corporate-700">
                        Session
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Cookies Analytics -->
                <div class="border border-corporate-200 dark:border-corporate-700 rounded-xl overflow-hidden">
                  <div class="p-4">
                    <div class="flex items-start justify-between gap-4">
                      <div class="flex-1">
                        <h3 class="font-semibold text-corporate-900 dark:text-white mb-1">
                          Cookies analytiques
                        </h3>
                        <p class="text-sm text-corporate-600 dark:text-corporate-400">
                          Ces cookies nous permettent de comprendre comment les visiteurs utilisent le site. 
                          Les données sont anonymisées et utilisées uniquement pour améliorer nos services.
                        </p>
                      </div>
                      <div class="flex-shrink-0">
                        <button
                          @click="localPreferences.analytics = !localPreferences.analytics"
                          :class="[
                            'w-12 h-7 rounded-full flex items-center px-1 transition-colors',
                            localPreferences.analytics 
                              ? 'bg-accent-500 justify-end' 
                              : 'bg-corporate-300 dark:bg-corporate-600 justify-start'
                          ]"
                        >
                          <div class="w-5 h-5 bg-white rounded-full shadow transition-transform"></div>
                        </button>
                      </div>
                    </div>
                    <div class="mt-3 flex flex-wrap gap-2">
                      <span class="px-2 py-1 text-xs bg-corporate-100 dark:bg-corporate-800 text-corporate-600 dark:text-corporate-400 rounded">
                        Cloudflare Analytics
                      </span>
                      <span class="px-2 py-1 text-xs bg-corporate-100 dark:bg-corporate-800 text-corporate-600 dark:text-corporate-400 rounded">
                        Mesure d'audience
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Cookies Préférences -->
                <div class="border border-corporate-200 dark:border-corporate-700 rounded-xl overflow-hidden">
                  <div class="p-4">
                    <div class="flex items-start justify-between gap-4">
                      <div class="flex-1">
                        <h3 class="font-semibold text-corporate-900 dark:text-white mb-1">
                          Cookies de préférences
                        </h3>
                        <p class="text-sm text-corporate-600 dark:text-corporate-400">
                          Ces cookies permettent de mémoriser vos préférences (thème, langue, paramètres d'affichage) 
                          pour personnaliser votre expérience lors de vos prochaines visites.
                        </p>
                      </div>
                      <div class="flex-shrink-0">
                        <button
                          @click="localPreferences.preferences = !localPreferences.preferences"
                          :class="[
                            'w-12 h-7 rounded-full flex items-center px-1 transition-colors',
                            localPreferences.preferences 
                              ? 'bg-accent-500 justify-end' 
                              : 'bg-corporate-300 dark:bg-corporate-600 justify-start'
                          ]"
                        >
                          <div class="w-5 h-5 bg-white rounded-full shadow transition-transform"></div>
                        </button>
                      </div>
                    </div>
                    <div class="mt-3 flex flex-wrap gap-2">
                      <span class="px-2 py-1 text-xs bg-corporate-100 dark:bg-corporate-800 text-corporate-600 dark:text-corporate-400 rounded">
                        Thème sombre/clair
                      </span>
                      <span class="px-2 py-1 text-xs bg-corporate-100 dark:bg-corporate-800 text-corporate-600 dark:text-corporate-400 rounded">
                        Sidebar
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Cookies Marketing (désactivé pour l'instant) -->
                <div class="border border-corporate-200 dark:border-corporate-700 rounded-xl overflow-hidden opacity-60">
                  <div class="p-4">
                    <div class="flex items-start justify-between gap-4">
                      <div class="flex-1">
                        <div class="flex items-center gap-2 mb-1">
                          <h3 class="font-semibold text-corporate-900 dark:text-white">
                            Cookies marketing
                          </h3>
                          <span class="px-2 py-0.5 text-xs font-medium bg-corporate-200 dark:bg-corporate-700 text-corporate-600 dark:text-corporate-400 rounded">
                            Non utilisés
                          </span>
                        </div>
                        <p class="text-sm text-corporate-600 dark:text-corporate-400">
                          Nous n'utilisons pas de cookies publicitaires ou de suivi tiers. 
                          Votre navigation reste privée.
                        </p>
                      </div>
                      <div class="flex-shrink-0">
                        <div class="w-12 h-7 bg-corporate-300 dark:bg-corporate-600 rounded-full flex items-center justify-start px-1 cursor-not-allowed">
                          <div class="w-5 h-5 bg-white rounded-full shadow"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              <!-- Info RGPD -->
              <div class="bg-accent-50 dark:bg-accent-900/20 rounded-xl p-4 border border-accent-200 dark:border-accent-800">
                <div class="flex gap-3">
                  <svg class="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div class="text-sm text-corporate-600 dark:text-corporate-400">
                    <p class="mb-2">
                      <strong class="text-corporate-900 dark:text-white">Vos droits RGPD :</strong>
                    </p>
                    <ul class="space-y-1">
                      <li>• Vous pouvez modifier vos choix à tout moment depuis les paramètres</li>
                      <li>• Votre consentement est valable 13 mois</li>
                      <li>• Vous pouvez exercer vos droits en contactant : <a href="mailto:dpo@laumeris.com" class="text-accent-600 hover:underline">dpo@laumeris.com</a></li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="sticky bottom-0 bg-white dark:bg-corporate-900 border-t border-corporate-200 dark:border-corporate-800 px-6 py-4">
              <div class="flex flex-col sm:flex-row gap-3 justify-between">
                <div class="flex gap-3">
                  <button
                    @click="rejectAll"
                    class="px-4 py-2 text-sm font-medium text-corporate-600 dark:text-corporate-400 hover:text-corporate-900 dark:hover:text-white transition-colors"
                  >
                    Tout refuser
                  </button>
                  <button
                    @click="acceptAll"
                    class="px-4 py-2 text-sm font-medium text-corporate-600 dark:text-corporate-400 hover:text-corporate-900 dark:hover:text-white transition-colors"
                  >
                    Tout accepter
                  </button>
                </div>
                <button
                  @click="savePreferences"
                  class="px-6 py-2.5 text-sm font-medium rounded-lg bg-gradient-to-r from-[#1E3A5F] to-[#152238] text-white hover:from-[#2a4a73] hover:to-[#1a2d47] transition-all shadow-lg"
                >
                  Enregistrer mes choix
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

